import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable, Modal, ActivityIndicator } from '../../lib/react-native-compat';
import { selectedJobDataAtom, userNameAtom, userEmailAtom } from '../../store/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { downloadPourCardPdf, archivePourCard, emailPourCard } from '../../db/company';
import { showToast } from '../../helpers/toast';

// Digital Concrete Pour Approval cards (paper form QUA-FRM-0007 Rev 1), reachable
// from the Quality checklist once Civil = Yes. Cards store as JSON rows in the
// hidden 'pourCards' section, so they ride the normal report save/read; the
// PDF/archive/email actions call their own endpoints (pourCard.js) with the
// card JSON, so they work as soon as the card is filled — no submit needed.
//
// Checklist keys here must match CHECK_SECTIONS/PLACEMENT_ITEMS in
// functions/src/functions/pourCard.js — that copy drives the PDF layout.

export const CHECK_SECTIONS = [
    ['Formwork', [
        ['formDimensions', 'Form Dimensions are Correct'],
        ['elevations', 'Elevations have been Verified'],
        ['chamfer', 'Chamfer Installed as per design'],
        ['formworkClean', 'Formwork is Clean and Free of Debris'],
        ['compaction', 'Compaction performed of subgrade as required'],
    ]],
    ['Reinforcing Steel', [
        ['clearances', 'Clearances are correct are per design'],
        ['rebarSpacing', 'Rebar spacing is as per drawings'],
        ['rebarSizes', 'Rebar sizes is as per drawings'],
        ['properLap', 'Proper lap is achieved'],
        ['rebarSecure', 'Rebar is secure'],
        ['rebarClean', 'Rebar is clean and free of excess rust?'],
    ]],
    ['Embedded Items', [
        ['anchorBolts', 'Anchor Bolts, Channel, Embed Plates are installed as per design'],
        ['pipeSleeves', 'Pipe Sleeves/Fittings Installed as Required'],
        ['conduitGrounding', 'Conduit/Grounding Installed as Required'],
    ]],
    ['Concrete Placement', [
        ['properHandling', 'Proper Handling during placement'],
        ['vibration', 'Vibration Performed'],
        ['liftThickness', 'Lift thickness is as per specifications'],
        ['correctFinish', 'Correct Finish as per design'],
    ]],
];

const TESTING_FIELDS = [
    ['estQty', 'Est Quantity (Cu YD)'],
    ['designPsi', 'Design Strength (PSI)'],
    ['cylindersRequired', '# of Cylinders Required'],
    ['logCylinders', 'Log # of Test Cylinders'],
    ['allowableSlump', 'Allowable Slump'],
    ['actualSlump', 'Actual Slump'],
    ['airContent', 'Air Content (%)'],
    ['ambientTemp', 'Ambient Temp (°F)'],
    ['concreteTemp', 'Concrete Temp (°F)'],
    ['batchToPour', 'Time from Batch to Pour'],
    ['batchTicket', 'Batch Ticket Number'],
    ['waterAdded', 'Water Added at Site (Gal)'],
];

const inputBase = {
    minHeight: 38,
    fontSize: 14,
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 10,
    paddingRight: 10,
    width: '100%',
    backgroundColor: '#fff',
    color: '#0F172A',
    boxSizing: 'border-box',
};

const labelStyle = {
    fontSize: 11,
    fontWeight: '600',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
};

const newCard = ({ report, jobData, userName }) => ({
    cardId: (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    client: jobData?.AR_AB_Name || jobData?.Client_Name || '',
    projectName: jobData?.BU_Description || jobData?.Job_Description || '',
    projectNo: report?.buId || '',
    workPackageNo: '',
    systemId: '',
    datePour: report?.reportDate || '',
    refDocs: ['', '', ''],
    location: '',
    checks: {},
    testing: {},
    comments: '',
    strikeRep: userName || '',
    strikeSignature: '',
    strikeSignDate: report?.reportDate || '',
    clientRep: '',
    clientSignature: '',
    clientSignDate: report?.reportDate || '',
    clientEmail: '',
    archivedUrl: '',
    archivedAt: '',
    sentTo: '',
    sentAt: '',
});

// ── Signature capture ────────────────────────────────────────────────────────
// Draw-to-sign canvas. A saved signature shows as an image with Re-sign /
// Clear; while signing, strokes accumulate on the canvas and Save snapshots
// it to a PNG data URL (multi-stroke signatures stay editable until saved).
const SignaturePad = ({ value, onChange, readOnly }) => {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const hasInk = useRef(false);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!editing || !canvasRef.current) return;
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#0F172A';
        hasInk.current = false;
    }, [editing]);

    const pos = (e) => {
        const r = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const down = (e) => {
        if (!canvasRef.current) return;
        e.preventDefault();
        canvasRef.current.setPointerCapture?.(e.pointerId);
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = pos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        // a dot for taps, so initials-style marks register
        ctx.lineTo(x + 0.1, y + 0.1);
        ctx.stroke();
        drawing.current = true;
        hasInk.current = true;
    };
    const move = (e) => {
        if (!drawing.current || !canvasRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = pos(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };
    const up = () => { drawing.current = false; };
    const clearCanvas = () => {
        const c = canvasRef.current;
        if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
        hasInk.current = false;
    };

    if (!editing) {
        return (
            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 10, flexWrap: 'wrap', rowGap: 6 }}>
                {value ? (
                    <img src={value} alt="signature" style={{ height: 48, maxWidth: 220, border: '1px solid #E2E8F0', borderRadius: 6, backgroundColor: '#fff' }} />
                ) : (
                    <Text style={{ fontSize: 13, color: '#94A3B8', fontStyle: 'italic' }}>Not signed</Text>
                )}
                {!readOnly && (
                    <Pressable onPress={() => setEditing(true)} style={{ cursor: 'pointer' }}>
                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#1D4ED8' }}>{value ? 'Re-sign' : 'Sign'}</Text>
                    </Pressable>
                )}
                {!readOnly && value ? (
                    <Pressable onPress={() => onChange('')} style={{ cursor: 'pointer' }}>
                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#B91C1C' }}>Clear</Text>
                    </Pressable>
                ) : null}
            </View>
        );
    }
    return (
        <View style={{ rowGap: 6 }}>
            <canvas
                ref={canvasRef}
                width={320}
                height={90}
                onPointerDown={down}
                onPointerMove={move}
                onPointerUp={up}
                onPointerLeave={up}
                style={{ border: '1px dashed #94A3B8', borderRadius: 8, backgroundColor: '#fff', touchAction: 'none', width: 320, maxWidth: '100%' }}
            />
            <View style={{ flexDirection: 'row', columnGap: 14 }}>
                <Pressable
                    onPress={() => {
                        if (hasInk.current && canvasRef.current) onChange(canvasRef.current.toDataURL('image/png'));
                        setEditing(false);
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#1D4ED8' }}>Save Signature</Text>
                </Pressable>
                <Pressable onPress={clearCanvas} style={{ cursor: 'pointer' }}>
                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#64748B' }}>Erase</Text>
                </Pressable>
                <Pressable onPress={() => setEditing(false)} style={{ cursor: 'pointer' }}>
                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#64748B' }}>Cancel</Text>
                </Pressable>
            </View>
        </View>
    );
};

// ✓ / N/A toggle for one checklist cell (tap again to clear).
const MarkToggle = ({ value, onChange, readOnly }) => (
    <View style={{ flexDirection: 'row', columnGap: 4 }}>
        {[['OK', '✓'], ['NA', 'N/A']].map(([v, label]) => (
            <Pressable
                key={v}
                disabled={readOnly}
                onPress={() => onChange(value === v ? '' : v)}
                style={{
                    minWidth: 38, minHeight: 32, alignItems: 'center', justifyContent: 'center', borderRadius: 6,
                    border: value === v ? '1px solid #1D4ED8' : '1px solid #CBD5E1',
                    backgroundColor: value === v ? '#DBEAFE' : '#fff',
                    cursor: readOnly ? 'default' : 'pointer',
                }}
            >
                <Text style={{ fontSize: 13, fontWeight: '700', color: value === v ? '#1D4ED8' : '#94A3B8' }}>{label}</Text>
            </Pressable>
        ))}
    </View>
);

const SectionTitle = ({ children }) => (
    <View style={{ backgroundColor: '#1E3A5F', borderRadius: 6, paddingTop: 5, paddingBottom: 5, paddingLeft: 10, paddingRight: 10, marginTop: 14, marginBottom: 8 }}>
        <Text style={{ fontSize: 12, fontWeight: '700', color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</Text>
    </View>
);

const ActionButton = ({ label, onPress, disabled, tone = 'primary' }) => {
    const tones = {
        primary: { bg: '#1D4ED8', border: '#1D4ED8', fg: '#fff' },
        neutral: { bg: '#fff', border: '#CBD5E1', fg: '#334155' },
    };
    const t = tones[tone];
    return (
        <Pressable
            onPress={onPress}
            disabled={disabled}
            style={{
                minHeight: 40, borderRadius: 8, alignItems: 'center', justifyContent: 'center',
                paddingLeft: 14, paddingRight: 14, border: `1px solid ${t.border}`,
                backgroundColor: disabled ? '#E2E8F0' : t.bg, cursor: disabled ? 'default' : 'pointer',
            }}
        >
            <Text style={{ fontSize: 13, fontWeight: '700', color: disabled ? '#94A3B8' : t.fg }}>{label}</Text>
        </Pressable>
    );
};

// Module-level (not inside the modal) so React keeps the same component type
// across renders — an inline definition would remount the inputs on every
// keystroke and drop focus.
const Field = ({ label, children, minWidth = 160 }) => (
    <View style={{ flex: 1, minWidth, marginBottom: 10 }}>
        <Text style={labelStyle}>{label}</Text>
        {children}
    </View>
);

const PourCardModal = ({ initial, onSave, onClose, readOnly, buId, jobName, userEmail }) => {
    const [card, setCard] = useState(initial);
    const [busy, setBusy] = useState('');
    // 'edit' | 'preview' — preview renders the generated PDF in an iframe so
    // the card reads exactly like the paper QUA-FRM-0007 before send/archive.
    const [view, setView] = useState('edit');
    const [previewUrl, setPreviewUrl] = useState(null);
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    // Every edit writes straight through to the report atom (onSave), so
    // closing the modal, switching tabs, or the wizard's next server save all
    // carry the card — there is no separate unsaved modal state to lose.
    const apply = (next) => { setCard(next); onSave(next); };
    const set = (patch) => apply({ ...card, ...patch });
    const setCheck = (key, patch) => apply({
        ...card,
        checks: { ...card.checks, [key]: { ...(card.checks?.[key] || {}), ...patch } },
    });
    const setTesting = (key, v) => apply({ ...card, testing: { ...card.testing, [key]: v } });

    // Actions render from the current draft; persist is a no-op safety since
    // apply() already wrote it through.
    const persist = (next) => { onSave(next || card); return next || card; };

    const doPreview = async () => {
        const c = persist();
        setBusy('pdf');
        const blob = await downloadPourCardPdf({ buId, jobName, card: c });
        setBusy('');
        if (!blob) return showToast('PDF generation failed — try again.', 'error');
        setPreviewUrl(URL.createObjectURL(blob));
        setView('preview');
    };

    const doArchive = async () => {
        const c = persist();
        setBusy('archive');
        const res = await archivePourCard({ buId, jobName, card: c });
        setBusy('');
        if (!res || res.error) return showToast(`Archive failed: ${res?.error || 'network error'}`, 'error');
        const next = { ...c, archivedUrl: res.webUrl || '', archivedAt: new Date().toISOString() };
        setCard(next);
        persist(next);
        showToast(`Archived to the job SharePoint site (${res.folder}).`, 'success');
    };

    const doEmail = async (to, busyKey = 'email') => {
        const c = persist();
        if (!to) return showToast(busyKey === 'emailMe' ? 'No email on your login.' : 'Enter a client email first.', 'error');
        setBusy(busyKey);
        const res = await emailPourCard({ buId, jobName, card: c, to });
        setBusy('');
        if (!res || res.error) return showToast(`Email failed: ${res?.error || 'network error'}`, 'error');
        const sentTo = (res.to || []).join(', ');
        const next = { ...c, sentTo, sentAt: new Date().toISOString() };
        setCard(next);
        persist(next);
        showToast(`Pour card emailed to ${sentTo}.`, 'success');
    };

    const textInput = (name, extra = {}) => (
        <input
            type={extra.type || 'text'}
            value={card[name] || ''}
            disabled={readOnly}
            onChange={(e) => set({ [name]: e.target.value })}
            style={inputBase}
        />
    );

    return (
        <Modal
            visible
            onRequestClose={onClose}
            style={{ width: 860, maxWidth: '96vw', maxHeight: '90vh', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', columnGap: 12, padding: 14, borderBottom: '1px solid #E2E8F0' }}>
                <View style={{ flexShrink: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A' }}>Concrete Pour Approval</Text>
                    <Text style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>Form QUA-FRM-0007 · {buId}</Text>
                </View>
                <View style={{ flexDirection: 'row', borderRadius: 8, border: '1px solid #CBD5E1', overflow: 'hidden', flexShrink: 0 }}>
                    {[['edit', 'Edit'], ['preview', 'PDF Preview']].map(([v, label]) => (
                        <Pressable
                            key={v}
                            disabled={!!busy}
                            onPress={() => { if (v === 'preview') doPreview(); else setView('edit'); }}
                            style={{
                                paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12,
                                backgroundColor: view === v ? '#1D4ED8' : '#fff', cursor: 'pointer',
                            }}
                        >
                            <Text style={{ fontSize: 12, fontWeight: '700', color: view === v ? '#fff' : '#475569' }}>
                                {v === 'preview' && busy === 'pdf' ? 'Building…' : label}
                            </Text>
                        </Pressable>
                    ))}
                </View>
                <Pressable onPress={onClose} style={{ minHeight: 32, minWidth: 32, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <Text style={{ fontSize: 16, color: '#64748B' }}>✕</Text>
                </Pressable>
            </View>

            {view === 'preview' ? (
                <View style={{ flexGrow: 1, minHeight: 0, backgroundColor: '#F1F5F9' }}>
                    {previewUrl ? (
                        <iframe
                            title="Pour card PDF preview"
                            src={previewUrl}
                            style={{ width: '100%', height: '68vh', border: 'none', display: 'block' }}
                        />
                    ) : (
                        <ActivityIndicator style={{ marginTop: 40 }} />
                    )}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 8, borderTop: '1px solid #E2E8F0', backgroundColor: '#fff' }}>
                        <Text style={{ fontSize: 12, color: '#64748B' }}>This is the exact PDF that archives to SharePoint / emails to the client.</Text>
                        {previewUrl ? (
                            <a href={previewUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#1D4ED8' }}>
                                Open in new tab
                            </a>
                        ) : null}
                    </View>
                </View>
            ) : (
            <div style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                <View style={{ padding: 14 }}>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 12 }}>
                        <Field label="Client">{textInput('client')}</Field>
                        <Field label="Project Name">{textInput('projectName')}</Field>
                    </View>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 12 }}>
                        <Field label="Project No" minWidth={120}>{textInput('projectNo')}</Field>
                        <Field label="Work Package No" minWidth={120}>{textInput('workPackageNo')}</Field>
                        <Field label="System ID" minWidth={120}>{textInput('systemId')}</Field>
                        <Field label="Date of Pour" minWidth={140}>{textInput('datePour', { type: 'date' })}</Field>
                    </View>

                    <Text style={labelStyle}>Reference Documents</Text>
                    {[0, 1, 2].map((i) => (
                        <input
                            key={i}
                            type="text"
                            placeholder={`${i + 1}.`}
                            value={(card.refDocs || [])[i] || ''}
                            disabled={readOnly}
                            onChange={(e) => {
                                const refDocs = [...(card.refDocs || ['', '', ''])];
                                refDocs[i] = e.target.value;
                                set({ refDocs });
                            }}
                            style={{ ...inputBase, marginBottom: 6 }}
                        />
                    ))}

                    <View style={{ marginTop: 8 }}>
                        <Text style={labelStyle}>Pour Location and Description</Text>
                        <textarea
                            value={card.location || ''}
                            disabled={readOnly}
                            onChange={(e) => set({ location: e.target.value })}
                            rows={3}
                            style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                        />
                    </View>

                    {CHECK_SECTIONS.map(([title, items]) => (
                        <View key={title}>
                            <SectionTitle>{title}</SectionTitle>
                            {items.map(([key, label], i) => {
                                const entry = card.checks?.[key] || {};
                                return (
                                    <View key={key} style={{ borderTop: i === 0 ? 'none' : '1px solid #F1F5F9', paddingTop: 8, paddingBottom: 8 }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', columnGap: 12, rowGap: 8 }}>
                                            <Text style={{ fontSize: 13, color: '#0F172A', flex: 1, minWidth: 200 }}>{label}</Text>
                                            <View style={{ flexDirection: 'row', columnGap: 16, alignItems: 'center' }}>
                                                <View>
                                                    <Text style={{ ...labelStyle, marginBottom: 2, textAlign: 'center' }}>Strike</Text>
                                                    <MarkToggle value={entry.strike || ''} onChange={(v) => setCheck(key, { strike: v })} readOnly={readOnly} />
                                                </View>
                                                <View>
                                                    <Text style={{ ...labelStyle, marginBottom: 2, textAlign: 'center' }}>Client</Text>
                                                    <MarkToggle value={entry.client || ''} onChange={(v) => setCheck(key, { client: v })} readOnly={readOnly} />
                                                </View>
                                            </View>
                                            <input
                                                type="text"
                                                placeholder="Remarks"
                                                value={entry.remarks || ''}
                                                disabled={readOnly}
                                                onChange={(e) => setCheck(key, { remarks: e.target.value })}
                                                style={{ ...inputBase, minHeight: 32, width: 180, flexShrink: 0 }}
                                            />
                                        </View>
                                    </View>
                                );
                            })}
                        </View>
                    ))}

                    <SectionTitle>Concrete Testing Data</SectionTitle>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 12 }}>
                        {TESTING_FIELDS.map(([key, label]) => (
                            <View key={key} style={{ width: 180, marginBottom: 10, flexGrow: 1, maxWidth: 260 }}>
                                <Text style={labelStyle}>{label}</Text>
                                <input
                                    type="text"
                                    value={card.testing?.[key] || ''}
                                    disabled={readOnly}
                                    onChange={(e) => setTesting(key, e.target.value)}
                                    style={inputBase}
                                />
                            </View>
                        ))}
                    </View>

                    <SectionTitle>Additional Comments / Notes</SectionTitle>
                    <textarea
                        value={card.comments || ''}
                        disabled={readOnly}
                        onChange={(e) => set({ comments: e.target.value })}
                        rows={3}
                        style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                    />

                    <SectionTitle>Signatures</SectionTitle>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 20, rowGap: 12 }}>
                        <View style={{ flex: 1, minWidth: 280 }}>
                            <Field label="Strike Representative">{textInput('strikeRep')}</Field>
                            <Field label="Date" minWidth={140}>{textInput('strikeSignDate', { type: 'date' })}</Field>
                            <SignaturePad value={card.strikeSignature} onChange={(v) => set({ strikeSignature: v })} readOnly={readOnly} />
                        </View>
                        <View style={{ flex: 1, minWidth: 280 }}>
                            <Field label="Client Representative">{textInput('clientRep')}</Field>
                            <Field label="Date" minWidth={140}>{textInput('clientSignDate', { type: 'date' })}</Field>
                            <SignaturePad value={card.clientSignature} onChange={(v) => set({ clientSignature: v })} readOnly={readOnly} />
                        </View>
                    </View>

                    <SectionTitle>Send / Archive</SectionTitle>
                    {(card.archivedUrl || card.sentTo) ? (
                        <View style={{ marginBottom: 8 }}>
                            {card.archivedUrl ? (
                                <Text style={{ fontSize: 12, color: '#166534' }}>
                                    Archived to SharePoint {card.archivedAt ? `on ${String(card.archivedAt).slice(0, 10)}` : ''} —{' '}
                                    <a href={card.archivedUrl} target="_blank" rel="noreferrer" style={{ color: '#1D4ED8' }}>open</a>
                                </Text>
                            ) : null}
                            {card.sentTo ? (
                                <Text style={{ fontSize: 12, color: '#166534' }}>
                                    Emailed to {card.sentTo} {card.sentAt ? `on ${String(card.sentAt).slice(0, 10)}` : ''}
                                </Text>
                            ) : null}
                        </View>
                    ) : null}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 10, rowGap: 8, alignItems: 'flex-end' }}>
                        <View style={{ minWidth: 220, flexGrow: 1, maxWidth: 320 }}>
                            <Text style={labelStyle}>Client Email</Text>
                            <input
                                type="email"
                                placeholder="name@client.com"
                                value={card.clientEmail || ''}
                                disabled={readOnly}
                                onChange={(e) => set({ clientEmail: e.target.value })}
                                style={inputBase}
                            />
                        </View>
                        <ActionButton label={busy === 'email' ? 'Sending…' : 'Email to Client'} onPress={() => doEmail(card.clientEmail)} disabled={!!busy || readOnly} />
                        <ActionButton label={busy === 'emailMe' ? 'Sending…' : 'Email to Me'} onPress={() => doEmail(userEmail, 'emailMe')} disabled={!!busy || readOnly} tone="neutral" />
                        <ActionButton label={busy === 'archive' ? 'Archiving…' : 'Archive to SharePoint'} onPress={doArchive} disabled={!!busy || readOnly} />
                        <ActionButton label={busy === 'pdf' ? 'Building…' : 'Preview PDF'} onPress={doPreview} disabled={!!busy} tone="neutral" />
                    </View>
                    {busy ? <ActivityIndicator style={{ marginTop: 10 }} /> : null}
                </View>
            </div>
            )}

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', columnGap: 10, padding: 12, borderTop: '1px solid #E2E8F0' }}>
                {!readOnly && (
                    <Text style={{ fontSize: 12, color: '#64748B', marginRight: 'auto' }}>
                        Changes save with the report automatically.
                    </Text>
                )}
                <ActionButton label={readOnly ? 'Close' : 'Done'} onPress={() => { if (!readOnly) persist(); onClose(); }} />
            </View>
        </Modal>
    );
};

const statusChip = (card) => {
    if (card.sentTo && card.archivedUrl) return { label: 'Archived · Emailed', bg: '#ECFDF5', fg: '#065F46' };
    if (card.archivedUrl) return { label: 'Archived', bg: '#ECFDF5', fg: '#065F46' };
    if (card.sentTo) return { label: 'Emailed', bg: '#EFF6FF', fg: '#1D4ED8' };
    return { label: 'Draft', bg: '#F1F5F9', fg: '#475569' };
};

// Card list + launcher, rendered under the Civil = Yes block of the Quality
// checklist.
const PourCards = ({ readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [jobData] = useAtom(selectedJobDataAtom);
    const [userName] = useAtom(userNameAtom);
    const [userEmail] = useAtom(userEmailAtom);
    const [openCard, setOpenCard] = useState(null);

    if (!report) return null;
    const cards = (report.sections?.pourCards || []).filter((c) => c && c.cardId);
    const jobName = jobData?.BU_Description || jobData?.Job_Description || '';

    // Older / partially filled cards open with the quick fields backfilled
    // (rep name + dates) so nothing has to be retyped.
    const withDefaults = (c) => (readOnly ? c : {
        ...c,
        strikeRep: c.strikeRep || userName || '',
        datePour: c.datePour || report.reportDate || '',
        strikeSignDate: c.strikeSignDate || c.datePour || report.reportDate || '',
        clientSignDate: c.clientSignDate || c.datePour || report.reportDate || '',
    });

    const saveCard = (card) => {
        const existing = report.sections?.pourCards || [];
        const idx = existing.findIndex((c) => c?.cardId === card.cardId);
        const next = idx >= 0
            ? existing.map((c) => (c?.cardId === card.cardId ? card : c))
            : [...existing, card];
        setReport({ ...report, sections: { ...report.sections, pourCards: next } });
        setDirty(true);
    };

    return (
        <View style={{ marginTop: 4, marginBottom: 10 }}>
            <Text style={{ fontSize: 12, fontWeight: '700', color: '#334155', marginBottom: 6 }}>Pour Cards</Text>
            {cards.map((c) => {
                const chip = statusChip(c);
                return (
                    <Pressable
                        key={c.cardId}
                        onPress={() => setOpenCard(withDefaults(c))}
                        style={{
                            flexDirection: 'row', alignItems: 'center', columnGap: 10, cursor: 'pointer',
                            border: '1px solid #E2E8F0', borderRadius: 8, backgroundColor: '#fff',
                            paddingTop: 8, paddingBottom: 8, paddingLeft: 10, paddingRight: 10, marginBottom: 6,
                        }}
                    >
                        <View style={{ flex: 1, minWidth: 0 }}>
                            <Text style={{ fontSize: 13, fontWeight: '600', color: '#0F172A' }} numberOfLines={1}>
                                {c.location || 'Pour card'}
                            </Text>
                            <Text style={{ fontSize: 11, color: '#64748B' }}>{c.datePour || ''}</Text>
                        </View>
                        <View style={{ borderRadius: 999, backgroundColor: chip.bg, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                            <Text style={{ fontSize: 11, fontWeight: '600', color: chip.fg }}>{chip.label}</Text>
                        </View>
                        <Text style={{ fontSize: 13, color: '#1D4ED8', fontWeight: '600' }}>{readOnly ? 'View' : 'Open'}</Text>
                    </Pressable>
                );
            })}
            {!readOnly && (
                <Pressable
                    onPress={() => setOpenCard(newCard({ report, jobData, userName }))}
                    style={{
                        alignSelf: 'flex-start', cursor: 'pointer', borderRadius: 8,
                        border: '1px dashed #94A3B8', paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12,
                    }}
                >
                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#1D4ED8' }}>+ Fill Out Pour Card</Text>
                </Pressable>
            )}
            {openCard && (
                <PourCardModal
                    initial={openCard}
                    onSave={saveCard}
                    onClose={() => setOpenCard(null)}
                    readOnly={readOnly}
                    buId={report.buId}
                    jobName={jobName}
                    userEmail={userEmail}
                />
            )}
        </View>
    );
};

export default PourCards;
